import * as React from 'react';

import { FeatureAppDefinition, FeatureServices } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import fetch from '@strictsoftware/fetch';
import { CarConfigurationServiceV2 } from '@volkswagen-onehub/car-configuration-feature-service';
import { CmsServiceV13Spi as CmsService } from '@volkswagen-onehub/cms-service';
import { ThemeProvider } from '@volkswagen-onehub/components-core';
import { DisclaimerManagerV1 as DisclaimerManager } from '@volkswagen-onehub/disclaimer-manager';
import { FaContentServiceV1 as FaContentService } from '@volkswagen-onehub/fa-content-service';
import { createFetchJson } from '@volkswagen-onehub/fetch-json';
import { FormsServiceV1 } from '@volkswagen-onehub/forms-service';
import { LayerManagerV25 } from '@volkswagen-onehub/layer-manager';
import { LocaleServiceV1 as LocaleService } from '@volkswagen-onehub/locale-service';
import { NavigationServiceV1 } from '@volkswagen-onehub/navigation-service';
import { ServiceConfigEndpointProvider } from '@volkswagen-onehub/service-config-endpoint-provider';
import { ServiceConfigProviderV1 } from '@volkswagen-onehub/service-config-provider';
import { ShowroomNavigationService } from '@volkswagen-onehub/showroom-navigation';
import { SingleConfigurationFlowNavigatorV1 } from '@volkswagen-onehub/single-configuration-flow-navigator';
import { TrackingManagerV1 as TrackingService } from '@volkswagen-onehub/tracking-service';
import { TrackingServiceConfigV1 } from '@volkswagen-onehub/tracking-service-config';
import { UserInteractionServiceV1 } from '@volkswagen-onehub/user-interaction-service';

import { createModelOverviewFeatureAppConfig } from './hooks/use-feature-app-config';
import { VisualizerFeatureAppDependencies } from './hooks/use-feature-services';

import { LoggingProvider } from './utils/logging';
import { resolveAbTestVariant } from './utils/resolve-ab-test-variant';

import App from './app';
import { FeatureAppContentContext, validateFeatureAppContent } from './context/feature-app-content';
import { CommonFeatureAppConfig, FeatureAppEnvironmentContext } from './context/feature-app-env.context';
import { FetchJsonContext } from './context/fetch-json.context';
import Environment from './environment';
import { featureAppStoreActions } from './store';

// @TODO broken type
export interface RequiredFeatureServicesInternal extends FeatureServices {
	'disclaimer-manager': DisclaimerManager;
	'locale-service': LocaleService;
	'service-config-provider': ServiceConfigProviderV1;
	'car-configuration': CarConfigurationServiceV2;
	'showroom-navigation': ShowroomNavigationService;
	tracking: TrackingService;
	'layer-manager': LayerManagerV25;
	'fa-content-service': FaContentService;
	'cms-service': CmsService;
	'single-configuration-flow-navigator': SingleConfigurationFlowNavigatorV1;
	'tracking-service-config': TrackingServiceConfigV1;
	'user-interaction-service': UserInteractionServiceV1;
	'service-config-endpoint-provider': ServiceConfigEndpointProvider;
	'navigation-service': NavigationServiceV1;
	'forms-service': FormsServiceV1;
}

let loadingPromiseResolve: (value: void | PromiseLike<void>) => void;
let loadingPromiseReject: (reason?: never) => void;

export const createLoadingPromise = (): Promise<void> => {
	return new Promise<void>((resolve, reject) => {
		loadingPromiseResolve = resolve;
		loadingPromiseReject = reject;
	});
};

export const resolveLoadingPromise = () => loadingPromiseResolve();
export const rejectLoadingPromise = (error?: never) => loadingPromiseReject(error);

const requiredFeatureServiceVersions: {
	[key in keyof RequiredFeatureServices]: string;
} = {
	's2:history': '^2.0.0',
	'disclaimer-manager': '^1.1.3',
	'locale-service': '^1.0.0',
	'service-config-provider': '^1.0.0',
	tracking: '^1.0.0',
	'layer-manager': '^2.5.0',
	'fa-content-service': '^1.0.0',
	'cms-service': '^1.3.0',
	'api-key-provider': '^1.0.0',
	'tracking-service-config': '^1.0.0',
	'service-config-endpoint-provider': '^1.0.0',
	'showroom-navigation': '^1.1.0',
	'user-interaction-service': '^1.0.0',
	'single-configuration-flow-navigator': '^1.2.0',
	'car-configuration': '^2.0.0',
	'data-version-service': '^1.3.0',
	'navigation-service': '^1.3.0',
	'forms-service': '^1.2.0'
};

export type RequiredFeatureServices = RequiredFeatureServicesInternal;

// @TODO double check types
const FeatureApp: FeatureAppDefinition<
	ReactFeatureApp,
	VisualizerFeatureAppDependencies,
	Partial<CommonFeatureAppConfig>
> = {
	dependencies: {
		featureServices: requiredFeatureServiceVersions
	},

	create: env => {
		const { featureServices } = env;

		const faContent = featureServices['fa-content-service'].content || {};

		const {
			'api-key-provider': apiKeyService,
			'fa-content-service': contentService,
			's2:logger': logger = console,
			'cms-service': cmsService
		} = featureServices;

		// @ts-ignore
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { featureApps, ...applicationEnvironment } = env.config!;

		// @ts-ignore
		// @TODO bad naming
		featureAppStoreActions.setFeatureServices(env, faContent);

		const isApplicationEnvironmentVisible = localStorage.getItem('isApplicationEnvironmentVisible') === 'true';

		if (isApplicationEnvironmentVisible) {
			// eslint-disable-next-line no-console
			console.table(applicationEnvironment);
		}

		// @ts-ignore
		// @TODO this should be gone!
		Environment.updateEnvironment({
			...applicationEnvironment,
			apiKey: apiKeyService?.apiKey
		});

		const fetchJson = createFetchJson({ applicationName: 'visualizer', logger, fetch });
		const config = createModelOverviewFeatureAppConfig(env.config);
		const { mockIds, mocksServerConfig } = config;

		if (mockIds) {
			fetchJson.initialize({
				mockIds: mockIds.split('-'),
				mocksServerConfig
			});
		}

		const validatedAppContent = validateFeatureAppContent(contentService.content);
		const abTestResolution = resolveAbTestVariant(validatedAppContent);
		const resolvedAppContent = {
			...validatedAppContent,
			gmcBundleId: abTestResolution.bundleId,
			gmcBundleVersion: abTestResolution.bundleVersion
		};
		featureAppStoreActions.applyAbTestResolution(abTestResolution);

		return {
			loadingPromise: createLoadingPromise(),
			render: () => (
				<FeatureAppEnvironmentContext.Provider value={{ ...env, config }}>
					<FeatureAppContentContext.Provider value={resolvedAppContent}>
						<FetchJsonContext.Provider value={fetchJson}>
							<LoggingProvider logger={logger}>
								<ThemeProvider theme="main" variant="v26">
									<App isEditorMode={cmsService.inEditor} />
								</ThemeProvider>
							</LoggingProvider>
						</FetchJsonContext.Provider>
					</FeatureAppContentContext.Provider>
				</FeatureAppEnvironmentContext.Provider>
			)
		};
	}
};

export default FeatureApp;
